/**
 * Analysis Tool Config Page (Admin)
 *
 * 管理分析 Tool 的 provider、jailbreak preset、參數設定
 */

import { useState, useEffect, useCallback } from 'react';
import {
  analysisConfigApi,
  type AnalysisToolConfig,
} from '../../services/api/novel-analysis';
import { API_BASE } from '../../services/api/client';

interface LLMProvider {
  id: string;
  display_name: string;
  provider_id: string;
  is_enabled: number;
}

interface JailbreakPreset {
  id: string;
  name: string;
  description: string | null;
}

interface NovelOption {
  id: string;
  title: string;
  total_chapters: number;
}

interface ChapterOption {
  id: string;
  chapter_index: number;
  title: string;
}

export default function AnalysisToolConfigPage() {
  const [configs, setConfigs] = useState<AnalysisToolConfig[]>([]);
  const [providers, setProviders] = useState<LLMProvider[]>([]);
  const [presets, setPresets] = useState<JailbreakPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [testingTool, setTestingTool] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, unknown> | null>(null);

  // Test selector state
  const [testSelectTool, setTestSelectTool] = useState<string | null>(null);
  const [novels, setNovels] = useState<NovelOption[]>([]);
  const [chapters, setChapters] = useState<ChapterOption[]>([]);
  const [selectedNovelId, setSelectedNovelId] = useState('');
  const [selectedChapterId, setSelectedChapterId] = useState('');
  const [loadingNovels, setLoadingNovels] = useState(false);
  const [loadingChapters, setLoadingChapters] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [configsData, providersRes, presetsRes] = await Promise.all([
        analysisConfigApi.getAll(),
        fetch(`${API_BASE}/admin/llm/providers`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${API_BASE}/admin/llm/jailbreak/presets`, { credentials: 'include' }).then((r) => r.json()),
      ]);
      setConfigs(configsData);
      // providers API returns { success, data: { providers: [...] } }
      const pData = (providersRes as { data?: { providers?: LLMProvider[] } }).data;
      setProviders((pData as { providers?: LLMProvider[] })?.providers || []);
      // presets API returns { success, data: { presets: [...] } }
      const prData = (presetsRes as { data?: { presets?: JailbreakPreset[] } }).data;
      setPresets((prData as { presets?: JailbreakPreset[] })?.presets || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleUpdate = async (toolName: string, field: string, value: unknown) => {
    setSaving(toolName);
    try {
      await analysisConfigApi.update(toolName, { [field]: value });
      setConfigs((prev) =>
        prev.map((c) => (c.tool_name === toolName ? { ...c, [field]: value, updated_at: new Date().toISOString() } : c))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSaving(null);
    }
  };

  // Open test selector modal: load novels first
  const openTestSelector = async (toolName: string) => {
    setTestSelectTool(toolName);
    setSelectedNovelId('');
    setSelectedChapterId('');
    setChapters([]);
    if (novels.length === 0) {
      setLoadingNovels(true);
      try {
        const res = await fetch(`${API_BASE}/novels?limit=100`, { credentials: 'include' }).then((r) => r.json());
        const data = (res as { data?: { novels?: NovelOption[] } | NovelOption[] }).data;
        const list = Array.isArray(data) ? data : (data as { novels?: NovelOption[] })?.novels || [];
        setNovels(list);
      } catch { /* ignore */ }
      setLoadingNovels(false);
    }
  };

  // Load chapters when novel selected
  const handleNovelSelect = async (novelId: string) => {
    setSelectedNovelId(novelId);
    setSelectedChapterId('');
    if (!novelId) { setChapters([]); return; }
    setLoadingChapters(true);
    try {
      const res = await fetch(`${API_BASE}/novels/${novelId}/chapters`, { credentials: 'include' }).then((r) => r.json());
      const data = (res as { data?: ChapterOption[] | { chapters?: ChapterOption[] } }).data;
      const list = Array.isArray(data) ? data : (data as { chapters?: ChapterOption[] })?.chapters || [];
      // Only show completed chapters (those that have content)
      setChapters(list.slice(0, 50)); // limit to 50 for performance
    } catch { /* ignore */ }
    setLoadingChapters(false);
  };

  // Execute test with selected novel + chapter
  const executeTest = async () => {
    if (!testSelectTool || !selectedNovelId || !selectedChapterId) return;
    const toolName = testSelectTool;
    setTestSelectTool(null);
    setTestingTool(toolName);
    setTestResult(null);

    try {
      await analysisConfigApi.test(
        toolName,
        { novel_id: selectedNovelId, chapter_id: selectedChapterId },
        {
          onEvent: (data) => {
            const event = data as Record<string, unknown>;
            if (event.type === 'result') {
              setTestResult(event);
            } else if (event.type === 'error') {
              setError(event.error as string);
            }
          },
          onError: (err) => setError(err.message),
          onComplete: () => setTestingTool(null),
        }
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Test failed');
      setTestingTool(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">分析 Tool 設定</h1>
        <button onClick={loadData} className="text-sm text-blue-600 hover:text-blue-800">
          重新載入
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">✕</button>
        </div>
      )}

      <div className="space-y-4">
        {configs.map((config) => (
          <div key={config.tool_name} className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="font-medium">{config.display_name}</h2>
                <p className="text-xs text-gray-500">{config.tool_name}</p>
                {config.description && <p className="text-sm text-gray-600 mt-1">{config.description}</p>}
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={config.is_enabled}
                    onChange={(e) => handleUpdate(config.tool_name, 'is_enabled', e.target.checked)}
                    className="rounded"
                  />
                  啟用
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Provider</label>
                <select
                  value={config.provider_id || ''}
                  onChange={(e) => handleUpdate(config.tool_name, 'provider_id', e.target.value || null)}
                  className="w-full text-sm border rounded px-2 py-1.5"
                  disabled={saving === config.tool_name}
                >
                  <option value="">未設定</option>
                  {providers.filter((p) => p.is_enabled).map((p) => (
                    <option key={p.id} value={p.id}>{p.display_name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Jailbreak Preset</label>
                <select
                  value={config.jailbreak_preset_id || ''}
                  onChange={(e) => handleUpdate(config.tool_name, 'jailbreak_preset_id', e.target.value || null)}
                  className="w-full text-sm border rounded px-2 py-1.5"
                  disabled={saving === config.tool_name}
                >
                  <option value="">無</option>
                  {presets.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Temperature</label>
                <input
                  type="number"
                  value={config.temperature}
                  onChange={(e) => handleUpdate(config.tool_name, 'temperature', parseFloat(e.target.value))}
                  min={0} max={2} step={0.1}
                  className="w-full text-sm border rounded px-2 py-1.5"
                  disabled={saving === config.tool_name}
                />
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Max Tokens</label>
                <input
                  type="number"
                  value={config.max_tokens}
                  onChange={(e) => handleUpdate(config.tool_name, 'max_tokens', parseInt(e.target.value))}
                  min={256} max={32768} step={256}
                  className="w-full text-sm border rounded px-2 py-1.5"
                  disabled={saving === config.tool_name}
                />
              </div>
            </div>

            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={() => openTestSelector(config.tool_name)}
                disabled={testingTool === config.tool_name || !config.provider_id}
                className="px-3 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 disabled:opacity-50"
              >
                {testingTool === config.tool_name ? '測試中...' : '測試'}
              </button>
              {saving === config.tool_name && (
                <span className="text-xs text-gray-400">儲存中...</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Test Selector Modal */}
      {testSelectTool && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium">選擇測試對象</h3>
              <button onClick={() => setTestSelectTool(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">小說</label>
                <select
                  value={selectedNovelId}
                  onChange={(e) => handleNovelSelect(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  <option value="">
                    {loadingNovels ? '載入中...' : '選擇小說'}
                  </option>
                  {novels.map((n) => (
                    <option key={n.id} value={n.id}>{n.title} ({n.total_chapters} 章)</option>
                  ))}
                </select>
              </div>
              {selectedNovelId && (
                <div>
                  <label className="block text-sm text-gray-600 mb-1">章節</label>
                  <select
                    value={selectedChapterId}
                    onChange={(e) => setSelectedChapterId(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                  >
                    <option value="">
                      {loadingChapters ? '載入中...' : '選擇章節'}
                    </option>
                    {chapters.map((ch) => (
                      <option key={ch.id} value={ch.id}>
                        {ch.title || `第 ${ch.chapter_index} 章`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setTestSelectTool(null)}
                  className="px-4 py-2 text-sm text-gray-600 border rounded hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  onClick={executeTest}
                  disabled={!selectedNovelId || !selectedChapterId}
                  className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  開始測試
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Result Modal */}
      {testResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium">測試結果</h3>
              <button onClick={() => setTestResult(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="text-sm">
              <p>成功：{testResult.success ? '是' : '否'}</p>
              {testResult.durationMs != null && <p>耗時：{String(testResult.durationMs)}ms</p>}
              {testResult.error != null && <p className="text-red-600">錯誤：{String(testResult.error)}</p>}
              {testResult.output != null && (
                <pre className="mt-2 bg-gray-50 p-3 rounded text-xs overflow-auto max-h-96">
                  {JSON.stringify(testResult.output, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
